import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { useAuth } from '../../../contexts/AuthContext';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import { getStatusBadgeClass, getStatusLabel } from '../../../utils/status';
import { generateAndUploadReceipt, generateAndUploadShippingForm } from '../../../utils/generateReceipt';

const UserDetail = () => {
  const { id } = useParams();
  // La navigation est gérée par les liens et boutons
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [shipments, setShipments] = useState([]);
  // Liste des membres de famille liés à cet utilisateur (users.id = family_members.parent_id)
  const [familyMembers, setFamilyMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [shipmentModal, setShipmentModal] = useState({ open: false, loading: false, shipment: null, error: null });
  const [documentModal, setDocumentModal] = useState({ open: false, title: '', url: '' });

  // État de la modale de création d'un membre de famille
  const [familyModal, setFamilyModal] = useState({ open: false, loading: false, error: null });
  // Formulaire d'ajout d'un membre de famille (schéma: family_members)
  const [familyForm, setFamilyForm] = useState({
    lastname: '',
    firstname: '',
    birth_date: '',
    email: '',
    phone: '',
  });

  // État de la modale de création d'un paiement (schéma: payments)
  // Permet d'ajouter un paiement côté admin directement depuis l'onglet Paiements.
  const [paymentModal, setPaymentModal] = useState({ open: false, loading: false, error: null });
  // Données du paiement (mêmes champs que ShipmentForm)
  const [paymentData, setPaymentData] = useState({
    amount: '',
    transaction_id: `TR-${Date.now()}`,
    payment_method: 'card',
    payment_status: 'pending',
    currency: 'EUR',
  });

  // Vérifier si l'utilisateur actuel est restreint
  const isRestrictedUser = currentUser?.email === 'contact@cgci-paris.com';
  
  // Classes et libellés de statut sont fournis par utils/status
  
  /**
   * Ouvre la modale d'ajout d'un membre de famille.
   * Remet le formulaire à zéro pour éviter les résidus d'état.
   */
  const handleOpenFamilyModal = () => {
    setFamilyForm({ lastname: '', firstname: '', birth_date: '', email: '', phone: '' });
    setFamilyModal({ open: true, loading: false, error: null });
  };
  /** Ferme la modale d'ajout d'un membre de famille */
  const handleCloseFamilyModal = () => {
    setFamilyModal(prev => ({ ...prev, open: false }));
  };
  /** Mise à jour des champs du formulaire de membre de famille */
  const updateFamilyForm = (e) => {
    const { name, value } = e.target;
    setFamilyForm(prev => ({ ...prev, [name]: value }));
  };
  
  /**
   * Crée un nouveau membre de famille lié à l'utilisateur courant (parent_id = users.id)
   * Validation minimale côté client puis insertion dans Supabase.
   */
  const handleCreateFamilyMember = async (e) => {
    e.preventDefault();
    setFamilyModal(prev => ({ ...prev, loading: true, error: null }));
    try {
      if (!familyForm.lastname.trim() || !familyForm.firstname.trim()) {
        throw new Error('Veuillez renseigner le nom et le prénom.');
      }
      const payload = {
        lastname: familyForm.lastname.trim(),
        firstname: familyForm.firstname.trim(),
        birth_date: familyForm.birth_date,
        email: familyForm.email.trim() || null,
        phone: familyForm.phone.trim() || null,
        parent_id: id,
      };
      const { error: insertError } = await supabase
        .from('family_members')
        .insert([payload]);
      if (insertError) throw insertError;
      toast.success('Membre de famille ajouté avec succès.');
      setFamilyModal(prev => ({ ...prev, open: false }));
      // Rafraîchir la liste des membres
      await fetchFamilyMembers();
    } catch (err) {
      console.error('Erreur création membre de famille:', err);
      const msg = err?.message || 'Une erreur est survenue lors de la création.';
      setFamilyModal(prev => ({ ...prev, error: msg }));
      toast.error(msg);
    } finally {
      setFamilyModal(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Handler pour ouvrir la modale et charger les infos d'expédition
  const handleShowShipment = async (shipmentId) => {
    setShipmentModal({ open: true, loading: true, shipment: null, error: null });
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('id', shipmentId)
        .single();
      if (error) {
        setShipmentModal({ open: true, loading: false, shipment: null, error: error.message });
        return;
      }
      setShipmentModal({ open: true, loading: false, shipment: data, error: null });
    } catch (err) {
      setShipmentModal({ open: true, loading: false, shipment: null, error: err.message });
    }
  };
  
  // Handler pour fermer la modale
  const handleCloseShipment = () => {
    setShipmentModal(prev => ({ ...prev, open: false }));
  };
  
  // Fonction pour fermer le modal de document
  const closeDocumentModal = () => {
    setDocumentModal({ ...documentModal, open: false });
  };
  
  // Fonction pour obtenir une URL signée pour un fichier dans le stockage Supabase
  const getSignedUrl = async (bucket, filename) => {
    try {
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .createSignedUrl(filename, 300); // URL valide pendant 5 minutes

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'URL signée pour ${filename}:`, error);
      throw error;
    }
  };
  
  // Fonction pour afficher un document
  const handleViewDocument = async (shipment, documentType) => {
    try {
      let url, title, filename;
      
      // Récupérer l'ID de la transaction de paiement associée à cette expédition
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('transaction_id')
        .eq('id', shipment.payment_id)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (documentType === 'receipt') {
        filename = `${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        title = 'Reçu de paiement';
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        title = 'Formulaire d\'expédition';
      }

      setDocumentModal({ open: true, title, url });
    } catch (error) {
      console.error('Erreur lors de l\'affichage du document:', error);
      toast.error(`Erreur lors de l'affichage du document. Le fichier n'existe peut-être pas.`);
    }
  };

  // Fonction pour télécharger un document
  const handleDownloadDocument = async (shipment, documentType) => {
    try {
      let url, filename, downloadName;
      
      // Récupérer l'ID de la transaction de paiement associée à cette expédition
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('transaction_id')
        .eq('id', shipment.payment_id)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (documentType === 'receipt') {
        filename = `${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        downloadName = `Reçu_${paymentData.transaction_id}.pdf`;
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        downloadName = filename;
      }

      // Télécharger le fichier
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Créer un lien temporaire pour le téléchargement
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      toast.error(`Erreur lors du téléchargement du document. Le fichier n'existe peut-être pas.`);
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBirthDate = (dateString) => {
    if (!dateString) return 'Non renseignée';
    const [year, month, day] = dateString.slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
  };

  // Formater le montant en euros
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  /**
   * Récupère la liste des membres de famille pour cet utilisateur.
   * Séparée pour pouvoir rafraîchir après création.
   */
  const fetchFamilyMembers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('family_members')
        .select('*')
        .eq('parent_id', id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFamilyMembers(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des membres de famille:', err);
      // Ne bloque pas l'écran; journalise l'erreur et affiche toast discret.
      toast.error("Impossible de charger les membres de famille.");
      setFamilyMembers([]);
    }
  }, [id]);

  /**
   * Récupère la liste des paiements pour cet utilisateur.
   * Séparée pour pouvoir rafraîchir après une création.
   */
  const fetchPayments = useCallback(async () => {
    try {
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select(`
          *,
          shipments (*)
        `)
        .eq('user_id', id)
        .order('created_at', { ascending: false });

      if (paymentsError) throw paymentsError;
      setPayments(paymentsData || []);
    } catch (err) {
      console.error('Erreur lors du chargement des paiements:', err);
      toast.error("Impossible de charger les paiements.");
      setPayments([]);
    }
  }, [id]);

  // Charger les données de l'utilisateur, ses paiements et expéditions
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les informations de l'utilisateur
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', id)
          .single();

        if (userError) throw userError;
        setUser(userData);

        // Récupérer les paiements de l'utilisateur avec les expéditions associées
        const { data: paymentsData, error: paymentsError } = await supabase
          .from('payments')
          .select(`
            *,
            shipments (*)
          `)
          .eq('user_id', id)
          .order('created_at', { ascending: false });

        if (paymentsError) throw paymentsError;
        
        // Extraire les paiements
        const formattedPayments = paymentsData || [];
        setPayments(formattedPayments);
        
        // Extraire et aplatir toutes les expéditions
        const allShipments = formattedPayments.flatMap(payment => 
          (payment.shipments || []).map(shipment => ({
            ...shipment,
            payment_amount: payment.amount,
            payment_currency: payment.currency,
            payment_date: payment.created_at
          }))
        );
        
        setShipments(allShipments);
        // Charger les membres de famille liés (family_members.parent_id = users.id)
        await fetchFamilyMembers();
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setError("Une erreur est survenue lors du chargement des données de l'utilisateur.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id, fetchFamilyMembers, fetchPayments]);

  /**
   * Ouvre la modale d'ajout d'un paiement.
   * Réinitialise le formulaire et génère un nouvel identifiant de transaction unique côté client.
   */
  const handleOpenPaymentModal = () => {
    setPaymentData({
      amount: '',
      transaction_id: `TR-${Date.now()}`,
      payment_method: 'card',
      payment_status: 'pending',
      currency: 'EUR',
    });
    setPaymentModal({ open: true, loading: false, error: null });
  };
  /** Ferme la modale d'ajout d'un paiement */
  const handleClosePaymentModal = () => {
    setPaymentModal(prev => ({ ...prev, open: false }));
  };
  // (les champs sont mis à jour directement via setPaymentData dans le formulaire)
  /**
   * Crée un nouveau paiement lié à l'utilisateur courant (user_id = users.id)
   * Valide côté client puis insertion dans Supabase.
   */
  const handleCreatePayment = async (e) => {
  e.preventDefault();
  setPaymentModal(prev => ({ ...prev, loading: true, error: null }));
  try {
    const amountNumber = Number(paymentData.amount);
    if (!amountNumber || amountNumber <= 0) {
      throw new Error('Veuillez saisir un montant valide (> 0).');
    }
    if (!id) {
      throw new Error("Identifiant utilisateur manquant.");
    }

    const payload = {
      user_id: id,
      transaction_id: paymentData.transaction_id,
      amount: amountNumber,
      currency: paymentData.currency || 'EUR',
      payment_status: paymentData.payment_status || 'pending',
      payment_method: paymentData.payment_method || 'card',
    };

    const { error: insertError } = await supabase
      .from('payments')
      .insert([payload])
      .select('*')
      .single();

    if (insertError) throw insertError;

    // Génération des documents comme dans Confirmation.js
    try {
      toast.loading('Génération des documents (reçu et formulaire)...', { id: 'doc-gen' });
      const paymentMethodLabel = payload.payment_method === 'card' ? 'Carte bancaire' : (payload.payment_method === 'cash' ? 'Espèces' : payload.payment_method);
      const userDetails = {
        transaction_id: payload.transaction_id,
        actTitle: 'Passeport', // Valeur par défaut côté admin
        amount: payload.amount,
        paymentMethod: paymentMethodLabel,
        userName: user ? `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Client' : 'Client',
        userLastName: user?.lastname || '',
        userFirstName: user?.firstname || '',
        userBirthDate: user?.birth_date || '',
        userEmail: user?.email || 'Non spécifié',
        userPhone: user?.phone || 'Non spécifié',
        userAddress: user?.address || 'Adresse non renseignée',
        userCity: user?.city || 'Ville non renseignée',
        userZipcode: user?.zipcode || 'Code postal non renseigné',
        userCountry: user?.country || 'Pays non renseigné',
        hostName: user?.host_name || '',
        withFamily: familyMembers.length > 0,
        familyMembers
      };

      // Uploader reçu et formulaire (même buckets et noms de fichiers)
      await generateAndUploadReceipt(userDetails);
      await generateAndUploadShippingForm(userDetails);
      toast.success('Documents générés avec succès.', { id: 'doc-gen' });
    } catch (docErr) {
      console.error('Erreur génération documents (admin):', docErr);
      toast.error("Paiement enregistré, mais la génération des documents a échoué.", { id: 'doc-gen' });
    }

    toast.success('Paiement créé avec succès.');
    setPaymentModal(prev => ({ ...prev, open: false }));
    // Rafraîchir la liste des paiements
    await fetchPayments();
  } catch (err) {
    console.error('Erreur création paiement:', err);
    const msg = err?.message || 'Une erreur est survenue lors de la création du paiement.';
    setPaymentModal(prev => ({ ...prev, error: msg }));
    toast.error(msg);
  } finally {
    setPaymentModal(prev => ({ ...prev, loading: false }));
  }
};
  

  

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">Utilisateur non trouvé</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div>
          <h1 className="mt-2 text-2xl font-bold text-gray-900 capitalize">
            {user.firstname} {user.lastname}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Enregistré(e) le {formatDate(user.created_at)}
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0">
          <Link
            to="/admin/users"
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Retour à la liste
          </Link>
          <div className="flex space-x-2">
            {!isRestrictedUser && (
              <Link
                to={`/admin/users/edit/${user.id}`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.793.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Modifier
              </Link>
            )}
            <Link
              to={`/admin/shipments/new?userId=${user.id}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Créer une expédition
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" role="tablist" aria-label="Sections utilisateur">
          <button
            onClick={() => setActiveTab('details')}
            className={`${activeTab === 'details' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            role="tab"
            aria-selected={activeTab === 'details'}
            aria-controls="tab-details"
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`${activeTab === 'payments' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            role="tab"
            aria-selected={activeTab === 'payments'}
            aria-controls="tab-payments"
          >
            Paiements ({payments.length})
          </button>
          <button
            onClick={() => setActiveTab('shipments')}
            className={`${activeTab === 'shipments' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            role="tab"
            aria-selected={activeTab === 'shipments'}
            aria-controls="tab-shipments"
          >
            Expéditions ({shipments.length})
          </button>
          <button
            onClick={() => setActiveTab('family')}
            className={`${activeTab === 'family' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            role="tab"
            aria-selected={activeTab === 'family'}
            aria-controls="tab-family"
          >
            Membres de famille ({familyMembers.length})
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="mt-6">
        {/* Onglet Informations */}
        {activeTab === 'details' && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100" id="tab-details" role="tabpanel" aria-labelledby="tab-details-button">
            <div className="px-6 py-6 bg-gradient-to-r from-green-50 to-green-100">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Informations personnelles
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Détails du profil de l&apos;utilisateur
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              {/* Carte d'identité */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow duration-300">
                <h4 className="text-sm uppercase tracking-wider text-gray-500 font-medium mb-3 border-b pb-2">Identité</h4>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs text-gray-500">Nom complet</p>
                      <p className="font-medium text-gray-900">{user.firstname} {user.lastname}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs text-gray-500">Date de naissance</p>
                      <p className="font-medium text-gray-900">{formatBirthDate(user.birth_date)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs text-gray-500">Email</p>
                      <a href={`mailto:${user.email}`} className="font-medium text-blue-600 hover:text-blue-800 transition-colors">
                        {user.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs text-gray-500">Téléphone</p>
                      <a href={`tel:${user.phone.replace(/\s/g, '')}`} className="font-medium text-purple-600 hover:text-purple-800 transition-colors">
                        {user.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Carte d'adresse */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow duration-300">
                <h4 className="text-sm uppercase tracking-wider text-gray-500 font-medium mb-3 border-b pb-2">Localisation</h4>
                
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs text-gray-500">Adresse</p>
                    <div className="mt-1 space-y-1">
                      <p className="text-gray-800">{user.address}</p>
                      <p className="text-gray-800">{user.zipcode} {user.city}</p>
                      <p className="text-gray-800">{user.country}</p>
                    </div>
                  </div>
                </div>
                
                {user.host_name && (
                  <div className="flex items-center mt-4 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs text-gray-500">Hébergeur</p>
                      <p className="font-medium text-gray-900">{user.host_name}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Carte d'inscription */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow duration-300 md:col-span-2">
                <h4 className="text-sm uppercase tracking-wider text-gray-500 font-medium mb-3 border-b pb-2">Informations du compte</h4>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs text-gray-500">Date d&apos;inscription</p>
                    <p className="font-medium text-gray-900">{formatDate(user.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Paiements */}
        {activeTab === 'payments' && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100" id="tab-payments" role="tabpanel" aria-labelledby="tab-payments-button">
            <div className="px-6 py-6 bg-gradient-to-r from-blue-50 to-blue-100">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center justify-between">
                <svg className="w-6 h-6 min-w-6 min-h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                <span className="flex items-center">Historique des paiements</span>
                <button
                  type="button"
                  onClick={handleOpenPaymentModal}
                  className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Ajouter un paiement
                </button>
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Tous les paiements effectués par cet utilisateur
              </p>
            </div>
            
            {payments.length === 0 ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
                  <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <p className="text-gray-500 text-lg">Aucun paiement trouvé pour cet utilisateur.</p>
              </div>
            ) : (
              <div className="overflow-x-auto p-4">
                <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        ID de transaction
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Méthode
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="relative px-6 py-4">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-blue-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 min-h-8 min-w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <svg className="h-4 w-4 min-h-4 min-w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{payment.transaction_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{formatCurrency(payment.amount)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            payment.payment_status === 'succeeded' ? 'bg-green-100 text-green-900' :
                            payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-900' :
                            'bg-red-100 text-red-900'
                          }`} role="status">
                            {payment.payment_status === 'succeeded' ? 'Payé' :
                             payment.payment_status === 'pending' ? 'En attente' :
                             'Échoué'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-6 w-6 min-h-6 min-w-6 rounded-md bg-purple-100 flex items-center justify-center mr-2">
                              <svg className="h-3 w-3 min-h-3 min-w-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700 capitalize">{payment.payment_method}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <svg className="h-4 w-4 min-h-4 min-w-4 text-gray-400 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                            <span className="text-sm text-gray-600">{formatDate(payment.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Link
                            to={`/admin/payments/${payment.id}`}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            aria-label={`Voir les détails du paiement ${payment.transaction_id}`}
                          >
                            <svg className="h-4 w-4 min-h-4 min-w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                            </svg>
                            Voir
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Onglet Expéditions */}
        {activeTab === 'shipments' && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100" id="tab-shipments" role="tabpanel" aria-labelledby="tab-shipments-button">
            <div className="px-6 py-6 bg-gradient-to-r from-amber-50 to-amber-100">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <svg className="w-6 h-6 min-w-6 min-h-6 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                </svg>
                Historique des expéditions
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Toutes les expéditions liées à cet utilisateur
              </p>
            </div>
            
            {shipments.length === 0 ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
                  <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <p className="text-gray-500 text-lg">Aucune expédition trouvée pour cet utilisateur.</p>
              </div>
            ) : (
              <div className="overflow-x-auto p-4">
                <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        N° de suivi
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Adresse d&apos;expédition
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Transporteur
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="relative px-6 py-4">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {shipments.map((shipment) => (
                      <tr key={shipment.id} className="hover:bg-amber-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                              <svg className="h-4 w-4 min-h-4 min-w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {shipment.tracking_number && shipment.tracking_number.startsWith('CD') 
                                  ? 'En attente' 
                                  : shipment.tracking_number}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <div className="h-8 w-8 rounded-md bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700 break-words">{shipment.recipient_address}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-6 w-6 rounded-md bg-teal-100 flex items-center justify-center mr-2">
                              <svg className="h-3 w-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700">{shipment.carrier}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipment.status)}`} role="status">
                            {getStatusLabel(shipment.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <svg className="h-4 w-4 min-h-4 min-w-4 text-gray-400 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                            <span className="text-sm text-gray-600">{shipment.delivery_date ? formatDate(shipment.delivery_date) : 'Non livré'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleShowShipment(shipment.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors"
                            aria-label={`Voir les détails de l'expédition ${shipment.tracking_number}`}
                          >
                            <svg className="-ml-0.5 mr-1.5 h-4 w-4 min-h-4 min-w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                            </svg>
                            Voir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Onglet Membres de famille */}
        {activeTab === 'family' && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100" id="tab-family" role="tabpanel" aria-labelledby="tab-family-button">
            <div className="px-6 py-6 bg-gradient-to-r from-emerald-50 to-emerald-100 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <svg className="w-6 h-6 min-w-6 min-h-6 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20h5v-2a4 4 0 00-5-3.87M7 7a4 4 0 118 0 4 4 0 01-8 0z" />
                  </svg>
                  Membres de famille
                </h3>
                <p className="mt-2 text-sm text-gray-600">Personnes rattachées à ce contact</p>
              </div>
              {!isRestrictedUser && (
                <button
                  type="button"
                  onClick={handleOpenFamilyModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter un membre
                </button>
              )}
            </div>

            {familyMembers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-4">
                  <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20h5v-2a4 4 0 00-5-3.87M7 7a4 4 0 118 0 4 4 0 01-8 0z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg">Aucun membre de famille pour ce contact.</p>
              </div>
            ) : (
              <div className="overflow-x-auto p-4">
                <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nom</th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Prénom</th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date de naissance</th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Téléphone</th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Créé le</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {familyMembers.map((fm) => (
                      <tr key={fm.id} className="hover:bg-emerald-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{fm.lastname}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">{fm.firstname}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">{formatBirthDate(fm.birth_date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {fm.email ? (
                            <a href={`mailto:${fm.email}`} className="text-blue-600 hover:text-blue-800">{fm.email}</a>
                          ) : (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {fm.phone ? (
                            <a href={`tel:${(fm.phone || '').replace(/\s/g, '')}`} className="text-purple-600 hover:text-purple-800">{fm.phone}</a>
                          ) : (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{formatDate(fm.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal pour afficher les détails d'expédition */}
      <Transition show={shipmentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={handleCloseShipment}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
                <div className="flex justify-between items-center bg-[#0b8e36] p-4 text-white">
                  <Dialog.Title as="h3" className="text-xl font-bold">
                    Détails de l&apos;expédition
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-white hover:text-gray-200 transition-colors"
                    onClick={handleCloseShipment}
                  >
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6 min-h-6 min-w-6" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="p-6">
                  {shipmentModal.loading ? (
                    <div className="flex justify-center items-center h-40">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#e88c00]"></div>
                    </div>
                  ) : shipmentModal.error ? (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-600 text-center font-medium">{shipmentModal.error}</div>
                  ) : shipmentModal.shipment ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {/* Colonne Statut & Tracking */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 min-h-6 min-w-6 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                          <span className="font-bold text-lg text-[#0b8e36]">Statut de l&apos;expédition</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Numéro de suivi</div>
                              <div className="font-semibold">
                                {shipmentModal.shipment.tracking_number && shipmentModal.shipment.tracking_number.startsWith('CD')
                                  ? 'En attente'
                                  : (shipmentModal.shipment.tracking_number || 'Non spécifié')}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" /><path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10" /><path d="M1 8h4" /><path d="M16 8h4M16 16h2a2 2 0 002-2v-3a4 4 0 00-4-4h-2" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Transporteur</div>
                              <div className="font-semibold">{shipmentModal.shipment.carrier || 'Non spécifié'}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Statut</div>
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipmentModal.shipment.status)}`}>
                                {getStatusLabel(shipmentModal.shipment.status)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Date d&apos;expédition</div>
                              <div className="font-semibold">{shipmentModal.shipment.shipping_date ? formatDate(shipmentModal.shipment.shipping_date) : 'Non spécifiée'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Destinataire */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 min-h-6 min-w-6 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Destinataire</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0112 0v2"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Nom</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_name}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z"/><circle cx="12" cy="11" r="2.5"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Adresse</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_address}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M22 16.92V19a2 2 0 01-2.18 2A19.72 19.72 0 013 5.18A2 2 0 015 3h2.09a2 2 0 012 1.72c.13.81.36 1.6.68 2.36a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l1.72-1.72a2 2 0 012.11-.45c.76.32 1.55.55 2.36.68a2 2 0 011.72 2V19z"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Téléphone</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_phone || 'Non spécifié'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Documents */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 min-h-6 min-w-6 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Documents à expédier</span>
                        </div>
                        
                        {(() => {
                          let docs = shipmentModal.shipment.documents_to_be_sent;
                          if (typeof docs === 'string') {
                            try {
                              docs = JSON.parse(docs);
                            } catch {
                              docs = [];
                            }
                          }
                          if (Array.isArray(docs) && docs.length > 0) {
                            return (
                              <div className="space-y-2">
                                {docs.map((doc, idx) => (
                                  <div key={doc.act_id || idx} className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                                    <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                                      <svg className="h-5 w-5 min-h-5 min-w-5 flex-shrink-0 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                                    </div>
                                    <div className="font-medium">{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center justify-center h-24 bg-white rounded-md shadow-sm">
                              <span className="text-gray-400 italic">Aucun document</span>
                            </div>
                          );
                        })()}
                        
                        {shipmentModal.shipment.return_status && (
                          <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-100 flex items-center gap-3">
                            <div className="bg-red-100 p-2 rounded-full">
                                <svg className="h-5 w-5 min-h-5 min-w-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 110 8h-1"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-red-500 font-medium">Statut de retour</div>
                              <div className="font-semibold text-red-600">Retourné {shipmentModal.shipment.return_reason && <span>({shipmentModal.shipment.return_reason})</span>}</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Documents à télécharger */}
                        <div className="mt-6 space-y-4">
                          {/* Reçu de paiement */}
                          <div className="border rounded-lg p-3 bg-white shadow-sm">
                            <h4 className="font-medium text-base mb-2 text-[#0b8e36]">Reçu de paiement</h4>
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => handleViewDocument(shipmentModal.shipment, 'receipt')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-h-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Voir
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(shipmentModal.shipment, 'receipt')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0b8e36] text-white rounded hover:bg-[#0b8e36]/90 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-h-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Télécharger
                              </button>
                            </div>
                          </div>
                          
                          {/* Formulaire d'expédition */}
                          <div className="border rounded-lg p-3 bg-white shadow-sm">
                            <h4 className="font-medium text-base mb-2 text-[#e88c00]">Formulaire d&apos;expédition</h4>
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => handleViewDocument(shipmentModal.shipment, 'shipping')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-h-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Voir
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(shipmentModal.shipment, 'shipping')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#e88c00] text-white rounded hover:bg-[#e88c00]/90 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-h-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Télécharger
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                
                <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100 mt-4">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-[#0b8e36] border border-transparent rounded-md hover:bg-[#097a2e] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0b8e36] transition-colors"
                    onClick={handleCloseShipment}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      {/* Modale: Ajouter un paiement */}
      <Transition appear show={paymentModal.open} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClosePaymentModal}>
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Ajouter un paiement</Dialog.Title>
                    <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClosePaymentModal}>
                      <span className="sr-only">Fermer</span>
                      <svg className="h-6 w-6 min-h-6 min-w-6" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {paymentModal.error && (
                    <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{paymentModal.error}</div>
                  )}
                  <form onSubmit={handleCreatePayment}>
                    <div className="p-4">
                      {/* Première ligne: Montant et ID de transaction */}
                      <div className="flex space-x-4 mb-4">
                        <div className="flex-1">
                          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="payment-amount">
                            Montant (€)
                          </label>
                          <input
                            type="number"
                            id="payment-amount"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            value={paymentData.amount}
                            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                            step="0.01"
                            min="0"
                            required
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="transaction-id">
                            ID de transaction
                          </label>
                          <input
                            type="text"
                            id="transaction-id"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-100"
                            value={paymentData.transaction_id}
                            readOnly
                          />
                        </div>
                      </div>

                      {/* Deuxième ligne: Méthode de paiement et Statut */}
                      <div className="flex space-x-4 mb-4">
                        <div className="flex-1">
                          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="payment-method">
                            Méthode de paiement
                          </label>
                          <select
                            id="payment-method"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            value={paymentData.payment_method}
                            onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                            required
                          >
                            <option value="card">Carte bancaire</option>
                            <option value="cash">Espèces</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="payment-status">
                            Statut du paiement
                          </label>
                          <select
                            id="payment-status"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            value={paymentData.payment_status}
                            onChange={(e) => setPaymentData({ ...paymentData, payment_status: e.target.value })}
                            required
                          >
                            <option value="pending">En attente</option>
                            <option value="succeeded">Effectuée</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100 mt-0">
                      <button type="button" className="mr-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500 transition-colors" onClick={handleClosePaymentModal}>
                        Annuler
                      </button>
                      <button type="submit" disabled={paymentModal.loading} className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-600 transition-colors ${paymentModal.loading ? 'bg-blue-600/70' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        {paymentModal.loading ? 'Création…' : 'Créer le paiement'}
                      </button>
                    </div>
                  </form>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      
      {/* Modal pour afficher les documents */}
      <Transition show={documentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeDocumentModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {documentModal.title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={closeDocumentModal}
                  >
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6 min-h-6 min-w-6" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mt-2 bg-gray-100 rounded-lg p-1 h-[70vh]">
                  <iframe
                    src={documentModal.url}
                    className="w-full h-full rounded"
                    title={documentModal.title}
                  />
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    onClick={closeDocumentModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Modale d'ajout d'un membre de famille */}
      <Transition show={familyModal.open} as={React.Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={handleCloseFamilyModal}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>
            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Ajouter un membre de famille</Dialog.Title>
                  <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleCloseFamilyModal}>
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6 min-h-6 min-w-6" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {familyModal.error && (
                  <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{familyModal.error}</div>
                )}
                <form onSubmit={handleCreateFamilyMember} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Nom *</label>
                      <input name="lastname" value={familyForm.lastname} onChange={updateFamilyForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Prénom *</label>
                      <input name="firstname" value={familyForm.firstname} onChange={updateFamilyForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Date de naissance *</label>
                      <input type="date" name="birth_date" value={familyForm.birth_date} max={new Date().toISOString().slice(0, 10)} onChange={updateFamilyForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                      <input type="email" name="email" value={familyForm.email} onChange={updateFamilyForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Téléphone</label>
                      <input name="phone" value={familyForm.phone} onChange={updateFamilyForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100 mt-4">
                    <button type="button" className="mr-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500 transition-colors" onClick={handleCloseFamilyModal}>
                      Annuler
                    </button>
                    <button type="submit" disabled={familyModal.loading} className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-600 transition-colors ${familyModal.loading ? 'bg-emerald-600/70' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                      {familyModal.loading ? 'Création…' : 'Créer membre'}
                    </button>
                  </div>
                </form>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default UserDetail;
